<template>
  <div>
    <h1 class="page-header">{{ $t('schedule.copy') }}</h1>
    <div class="row">
      <div class="col-sm-12">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loadingPreData">
              <form class="form-horizontal">
                <div class="row mb-3">
                  <div class="col-sm-12 col-lg-6 col-md-12">
                    <!-- Copy From (readonly) -->
                    <div class="form-group row">
                      <label class="ml-2 form-col-label control-label required">
                        {{ $t('schedule.copyFrom') }}
                      </label>
                      <div class="col-md-12">
                        <input
                          type="text"
                          class="form-control"
                          :value="model.from_class_name"
                          readonly
                        />
                        <input type="hidden" v-model="model.copy_from_sch_id" />
                      </div>
                    </div>

                    <!-- Copy To -->
                    <div class="form-group row">
                      <label class="ml-2 form-col-label control-label required">
                        {{ $t('schedule.copyTo') }}
                      </label>
                      <div class="col-md-12">
                        <div class="copy-select-wrapper">
                          <input
                            type="text"
                            class="form-control"
                            v-model="classSearch"
                            :placeholder="$t('typeForSearch')"
                            @focus="dropdownOpen = true"
                            @blur="onBlur"
                            @input="dropdownOpen = true"
                          />
                          <div
                            v-if="dropdownOpen && !loadingClasses"
                            class="copy-dropdown"
                          >
                            <div
                              v-if="filteredClasses.length === 0"
                              class="copy-dropdown-empty"
                            >
                              {{
                                classSearch
                                  ? $t('noData')
                                  : $t('schedule.noFutureTerm')
                              }}
                            </div>
                            <div
                              v-for="item in filteredClasses"
                              :key="item.class_id"
                              class="copy-dropdown-item"
                              :class="{
                                'is-selected':
                                  model.copy_to_class_id === item.class_id
                              }"
                              @mousedown.prevent="selectClass(item)"
                            >
                              {{ item.label }}
                            </div>
                          </div>
                          <div v-if="loadingClasses" class="copy-dropdown">
                            <div class="copy-dropdown-empty">
                              <i class="fas fa-spinner fa-spin"></i>
                              {{ $t('loading') }}...
                            </div>
                          </div>
                        </div>
                        <div
                          class="validate"
                          v-if="errors.has('copy_to_class_id')"
                        >
                          {{ errors.first('copy_to_class_id') }}
                        </div>
                      </div>
                    </div>

                    <!-- Buttons -->
                    <div class="form-group row text-right">
                      <label
                        class="col-md-4 form-col-label control-label"
                      ></label>
                      <div class="col-md-7 text-right">
                        <ts-button
                          class="btn-primary mr-2"
                          @click.prevent="save"
                          :waiting="saving"
                        >
                          {{ $t('save') }}
                        </ts-button>
                        <ts-button
                          class="btn-default ml-2"
                          @click="$router.push({ name: 'schedule' })"
                        >
                          {{ $t('cancel') }}
                        </ts-button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions } from 'vuex'
import _ from 'lodash'

export default {
  name: 'schedule-copy-schedule',
  props: ['schedule'],
  data() {
    return {
      model: {
        from_class_name: null,
        copy_from_sch_id: null,
        copy_to_class_id: null
      },
      errors: new Errors(),
      loadingPreData: false,
      loadingClasses: false,
      saving: false,
      allClasses: [],
      classSearch: '',
      dropdownOpen: false
    }
  },
  computed: {
    filteredClasses() {
      if (!this.classSearch.trim()) return this.allClasses
      const q = this.classSearch.toLowerCase()
      return this.allClasses.filter(c => c.label.toLowerCase().includes(q))
    }
  },
  created() {
    this.setEditData()
  },
  mounted() {
    this.fetchClasses()
  },
  methods: {
    ...mapActions('schedule', ['getClassForCopy']),

    async fetchClasses() {
      if (!this.model.copy_from_sch_id) return
      this.loadingClasses = true
      try {
        const response = await this.getClassForCopy({
          copy_from_sch_id: this.model.copy_from_sch_id
        })
        if (!response.data.length) {
          this.$toasted.info('No future term available for this class')
        }
        this.allClasses = response.data.map(el => ({
          class_id: el.class_id,
          label: el.class_name,
          acps_id: el.acps_id
        }))
      } catch (error) {
        this.$toasted.error(error.message)
      } finally {
        this.loadingClasses = false
      }
    },

    selectClass(item) {
      this.model.copy_to_class_id = item.class_id
      this.classSearch = item.label
      this.dropdownOpen = false
    },

    onBlur() {
      setTimeout(() => {
        this.dropdownOpen = false
        if (!this.model.copy_to_class_id) {
          this.classSearch = ''
        }
      }, 200)
    },

    // Position the fixed dropdown directly below the input element
    positionDropdown() {
      this.$nextTick(() => {
        const input = this.$el.querySelector('.copy-select-wrapper input')
        const dropdown = this.$el.querySelector('.copy-dropdown')
        if (!input || !dropdown) return
        const rect = input.getBoundingClientRect()
        dropdown.style.top = rect.bottom + 4 + 'px'
        dropdown.style.left = rect.left + 'px'
        dropdown.style.width = rect.width + 'px'
        dropdown.style.minWidth = Math.max(rect.width, 400) + 'px'
      })
    },

    setEditData() {
      this.model.from_class_name = this.$route.params.class_name || ''
      this.model.copy_from_sch_id = this.$route.params.sch_id || null
      this.model.copy_to_class_id = null
      this.classSearch = ''
    },

    async save() {
      this.errors = new Errors()

      if (!this.model.copy_to_class_id) {
        this.$toasted.error('Please select a class to copy to')
        return
      }

      this.saving = true
      this.$store
        .dispatch('schedule/copySchedule', {
          copy_from_sch_id: this.model.copy_from_sch_id,
          copy_to_class_id: this.model.copy_to_class_id
        })
        .then(response => {
          this.clearForm()
          this.saving = false
          this.$toasted.success(response.message)
          this.$router.push({
            name: 'schedule',
            query: { copied: '1' }
          })
        })
        .catch(error => {
          this.saving = false
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
    },

    clearForm() {
      this.model = {
        from_class_name: null,
        copy_from_sch_id: null,
        copy_to_class_id: null
      }
      this.classSearch = ''
      this.allClasses = []
    }
  },
  beforeRouteLeave(to, from, next) {
    if (_.isEmpty(to.params) && to.name != 'schedule') {
      this.$store.commit('schedule/RECEIVE_CLASSES', [])
      this.$store.commit('schedule/SET_EDIT_DATA', {})
    }
    next()
  }
}
</script>

<style lang="scss" scoped>
.form-col-label {
  text-align: left !important;
}
.copy-select-wrapper {
  position: relative;
}

.copy-dropdown {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  min-width: 400px;
  max-width: 600px;
}

.copy-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  color: #333;
  font-size: 13px;
  white-space: nowrap;
  border-bottom: 1px solid #f5f5f5;

  &:hover {
    background-color: #e6f7ff;
  }

  &.is-selected {
    background-color: #bae7ff;
    font-weight: 500;
  }

  &:last-child {
    border-bottom: none;
  }
}

.copy-dropdown-empty {
  padding: 10px 12px;
  color: #999;
  font-size: 13px;
  text-align: center;
}
</style>
